import React from "react";

// --- Utility: Date Formatter ---
const formatDate = (dateStr: string) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

interface AdmissionInvoiceProps {
  admissionData: any;
  signinuser: any;
}

const AdmissionInvoice = React.forwardRef<HTMLDivElement, AdmissionInvoiceProps>(
  ({ admissionData, signinuser }, ref) => {
    if (!admissionData) return null;

    const paidFromEmis =
      admissionData.installDetails
        ?.filter((e: any) => e.isPaid)
        .reduce((acc: number, curr: any) => acc + curr.amount, 0) || 0;
    const totalPaid = (admissionData.downPayment || 0) + paidFromEmis;
    const totalPending = (admissionData.totalCoursePrice || 0) - totalPaid;

    const companyName = signinuser?.Name || "Academy";
    const companyEmail = signinuser?.Email || "";
    const companyPhone = signinuser?.Phone || "";
    const companyLogo = signinuser?.ComapanyImageOne || "";

    const statusColor =
      admissionData.admStatus === "Approved"
        ? "#16a34a"
        : admissionData.admStatus === "Rejected"
        ? "#dc2626"
        : "#ea580c";

    return (
      <div
        ref={ref}
        style={{
          width: "210mm",
          minHeight: "297mm",
          padding: "0",
          background: "#ffffff",
          fontFamily: "'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
          color: "#1a1a2e",
          fontSize: "13px",
          lineHeight: "1.5",
          boxSizing: "border-box",
        }}
      >
        {/* Header Band */}
        <div
          style={{
            background: "linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #6366f1 100%)",
            padding: "28px 40px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            {companyLogo && (
              <img
                src={companyLogo}
                alt="Logo"
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "12px",
                  objectFit: "cover",
                  border: "2px solid rgba(255,255,255,0.3)",
                }}
              />
            )}
            <div>
              <div
                style={{
                  fontSize: "22px",
                  fontWeight: 800,
                  color: "#ffffff",
                  letterSpacing: "-0.5px",
                }}
              >
                {companyName}
              </div>
              <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.8)", marginTop: "2px" }}>
                {companyEmail} {companyPhone && `• ${companyPhone}`}
              </div>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div
              style={{
                fontSize: "28px",
                fontWeight: 800,
                color: "#ffffff",
                letterSpacing: "2px",
                textTransform: "uppercase",
              }}
            >
              INVOICE
            </div>
            <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.8)", marginTop: "4px" }}>
              #{admissionData._id?.slice(-8).toUpperCase() || "N/A"}
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "30px 40px" }}>
          {/* Status + Date Row */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "28px",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: `${statusColor}15`,
                color: statusColor,
                fontWeight: 700,
                fontSize: "12px",
                padding: "6px 16px",
                borderRadius: "20px",
                border: `1.5px solid ${statusColor}40`,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              ● {admissionData.admStatus || "Pending"}
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 600 }}>
                ADMISSION DATE
              </div>
              <div style={{ fontSize: "14px", fontWeight: 700, color: "#1e40af" }}>
                {formatDate(admissionData.admDate)}
              </div>
            </div>
          </div>

          {/* Bill To + Course Info */}
          <div
            style={{
              display: "flex",
              gap: "24px",
              marginBottom: "28px",
            }}
          >
            {/* Bill To */}
            <div
              style={{
                flex: 1,
                background: "#f8fafc",
                borderRadius: "12px",
                padding: "20px",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  marginBottom: "10px",
                }}
              >
                Bill To
              </div>
              <div style={{ fontSize: "16px", fontWeight: 700, color: "#1e293b", marginBottom: "6px" }}>
                {admissionData.name}
              </div>
              <div style={{ fontSize: "12px", color: "#64748b", lineHeight: "1.8" }}>
                <div>✉ {admissionData.email || "N/A"}</div>
                <div>☎ {admissionData.mobileNo || "N/A"}</div>
                {admissionData.altMobileNo && <div>☎ {admissionData.altMobileNo} (Alt)</div>}
                {admissionData.address && <div>⌂ {admissionData.address}</div>}
              </div>
            </div>

            {/* Course & Branch Info */}
            <div
              style={{
                flex: 1,
                background: "#f8fafc",
                borderRadius: "12px",
                padding: "20px",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  marginBottom: "10px",
                }}
              >
                Course Details
              </div>
              <div style={{ fontSize: "12px", color: "#64748b", lineHeight: "2" }}>
                <div>
                  <span style={{ fontWeight: 600, color: "#475569" }}>Course:</span>{" "}
                  {admissionData.courseId?.CourseName || "N/A"}
                </div>
                <div>
                  <span style={{ fontWeight: 600, color: "#475569" }}>Branch:</span>{" "}
                  {admissionData.branchId?.BranchName || "N/A"}
                </div>
                <div>
                  <span style={{ fontWeight: 600, color: "#475569" }}>Counsellor:</span>{" "}
                  {admissionData.counsellorId?.Name || "N/A"}
                </div>
                <div>
                  <span style={{ fontWeight: 600, color: "#475569" }}>Approver:</span>{" "}
                  {admissionData.approverId?.Name || "N/A"}
                </div>
              </div>
            </div>
          </div>

          {/* Course Fee Table */}
          <div style={{ marginBottom: "24px" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                borderRadius: "12px",
                overflow: "hidden",
                border: "1px solid #e2e8f0",
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "linear-gradient(135deg, #1e40af, #3b82f6)",
                  }}
                >
                  <th
                    style={{
                      padding: "12px 20px",
                      textAlign: "left",
                      color: "#ffffff",
                      fontSize: "11px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Description
                  </th>
                  <th
                    style={{
                      padding: "12px 20px",
                      textAlign: "center",
                      color: "#ffffff",
                      fontSize: "11px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Payment Type
                  </th>
                  <th
                    style={{
                      padding: "12px 20px",
                      textAlign: "center",
                      color: "#ffffff",
                      fontSize: "11px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Payment Mode
                  </th>
                  <th
                    style={{
                      padding: "12px 20px",
                      textAlign: "right",
                      color: "#ffffff",
                      fontSize: "11px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: "#ffffff" }}>
                  <td
                    style={{
                      padding: "14px 20px",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#1e293b",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    {admissionData.courseId?.CourseName || "Course Fee"}
                  </td>
                  <td
                    style={{
                      padding: "14px 20px",
                      textAlign: "center",
                      fontSize: "12px",
                      color: "#64748b",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    {admissionData.paymentType || "Full"}
                  </td>
                  <td
                    style={{
                      padding: "14px 20px",
                      textAlign: "center",
                      fontSize: "12px",
                      color: "#64748b",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    {admissionData.paymentMode || "Manual"}
                  </td>
                  <td
                    style={{
                      padding: "14px 20px",
                      textAlign: "right",
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#1e293b",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    ₹{admissionData.totalCoursePrice?.toLocaleString("en-IN") || 0}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Financial Summary Cards */}
          <div
            style={{
              display: "flex",
              gap: "16px",
              marginBottom: "28px",
            }}
          >
            <div
              style={{
                flex: 1,
                background: "#eff6ff",
                borderRadius: "10px",
                padding: "16px 20px",
                border: "1px solid #bfdbfe",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "10px", fontWeight: 700, color: "#3b82f6", textTransform: "uppercase" }}>
                Down Payment
              </div>
              <div style={{ fontSize: "20px", fontWeight: 800, color: "#1e40af", marginTop: "4px" }}>
                ₹{(admissionData.downPayment || 0).toLocaleString("en-IN")}
              </div>
            </div>
            <div
              style={{
                flex: 1,
                background: "#f0fdf4",
                borderRadius: "10px",
                padding: "16px 20px",
                border: "1px solid #bbf7d0",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "10px", fontWeight: 700, color: "#16a34a", textTransform: "uppercase" }}>
                Total Paid
              </div>
              <div style={{ fontSize: "20px", fontWeight: 800, color: "#15803d", marginTop: "4px" }}>
                ₹{totalPaid.toLocaleString("en-IN")}
              </div>
            </div>
            <div
              style={{
                flex: 1,
                background: "#fef2f2",
                borderRadius: "10px",
                padding: "16px 20px",
                border: "1px solid #fecaca",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "10px", fontWeight: 700, color: "#dc2626", textTransform: "uppercase" }}>
                Balance Due
              </div>
              <div style={{ fontSize: "20px", fontWeight: 800, color: "#b91c1c", marginTop: "4px" }}>
                ₹{totalPending.toLocaleString("en-IN")}
              </div>
            </div>
          </div>

          {/* EMI Schedule Table */}
          {admissionData.paymentType === "Installment" &&
            admissionData.installDetails?.length > 0 && (
              <div style={{ marginBottom: "28px" }}>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#1e293b",
                    marginBottom: "12px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Installment Schedule
                </div>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    borderRadius: "10px",
                    overflow: "hidden",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <thead>
                    <tr style={{ background: "#f1f5f9" }}>
                      <th
                        style={{
                          padding: "10px 16px",
                          textAlign: "left",
                          fontSize: "10px",
                          fontWeight: 700,
                          color: "#64748b",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        #
                      </th>
                      <th
                        style={{
                          padding: "10px 16px",
                          textAlign: "left",
                          fontSize: "10px",
                          fontWeight: 700,
                          color: "#64748b",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Label
                      </th>
                      <th
                        style={{
                          padding: "10px 16px",
                          textAlign: "center",
                          fontSize: "10px",
                          fontWeight: 700,
                          color: "#64748b",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Due Date
                      </th>
                      <th
                        style={{
                          padding: "10px 16px",
                          textAlign: "right",
                          fontSize: "10px",
                          fontWeight: 700,
                          color: "#64748b",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Amount
                      </th>
                      <th
                        style={{
                          padding: "10px 16px",
                          textAlign: "center",
                          fontSize: "10px",
                          fontWeight: 700,
                          color: "#64748b",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Status
                      </th>
                      <th
                        style={{
                          padding: "10px 16px",
                          textAlign: "center",
                          fontSize: "10px",
                          fontWeight: 700,
                          color: "#64748b",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Paid Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {admissionData.installDetails.map((emi: any, idx: number) => (
                      <tr
                        key={idx}
                        style={{
                          background: idx % 2 === 0 ? "#ffffff" : "#f8fafc",
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        <td
                          style={{
                            padding: "10px 16px",
                            fontSize: "12px",
                            color: "#94a3b8",
                            fontWeight: 600,
                          }}
                        >
                          {idx + 1}
                        </td>
                        <td
                          style={{
                            padding: "10px 16px",
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#1e293b",
                          }}
                        >
                          {emi.label}
                        </td>
                        <td
                          style={{
                            padding: "10px 16px",
                            textAlign: "center",
                            fontSize: "12px",
                            color: "#64748b",
                          }}
                        >
                          {formatDate(emi.installDate)}
                        </td>
                        <td
                          style={{
                            padding: "10px 16px",
                            textAlign: "right",
                            fontSize: "13px",
                            fontWeight: 700,
                            color: "#1e293b",
                          }}
                        >
                          ₹{emi.amount?.toLocaleString("en-IN")}
                        </td>
                        <td style={{ padding: "10px 16px", textAlign: "center" }}>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "3px 12px",
                              borderRadius: "12px",
                              fontSize: "10px",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              background: emi.isPaid ? "#dcfce7" : "#fef3c7",
                              color: emi.isPaid ? "#15803d" : "#b45309",
                              border: `1px solid ${emi.isPaid ? "#86efac" : "#fcd34d"}`,
                            }}
                          >
                            {emi.isPaid ? "Paid" : "Pending"}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "10px 16px",
                            textAlign: "center",
                            fontSize: "11px",
                            color: "#64748b",
                          }}
                        >
                          {emi.isPaid && emi.paidDate ? formatDate(emi.paidDate) : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

          {/* Bank Account Info */}
          {admissionData.paymentAccount && (
            <div
              style={{
                background: "#f8fafc",
                borderRadius: "10px",
                padding: "16px 20px",
                border: "1px solid #e2e8f0",
                marginBottom: "28px",
              }}
            >
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  marginBottom: "8px",
                }}
              >
                Payment Account
              </div>
              <div style={{ fontSize: "12px", color: "#475569" }}>
                <span style={{ fontWeight: 600 }}>
                  {admissionData.paymentAccount?.bankName || "N/A"}
                </span>{" "}
                — A/C: {admissionData.paymentAccount?.accountNo || "N/A"}
              </div>
            </div>
          )}

          {/* Remark */}
          {admissionData.remark && (
            <div
              style={{
                background: "#fffbeb",
                borderRadius: "10px",
                padding: "14px 20px",
                border: "1px solid #fef3c7",
                marginBottom: "28px",
              }}
            >
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  color: "#b45309",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  marginBottom: "4px",
                }}
              >
                Remark
              </div>
              <div style={{ fontSize: "12px", color: "#78350f" }}>
                {admissionData.remark}
              </div>
            </div>
          )}

          {/* Footer */}
          <div
            style={{
              marginTop: "40px",
              borderTop: "2px solid #e2e8f0",
              paddingTop: "20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
            }}
          >
            <div>
              <div style={{ fontSize: "11px", color: "#94a3b8", marginBottom: "4px" }}>
                Generated on {new Date().toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })} at {new Date().toLocaleTimeString("en-IN", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
              <div style={{ fontSize: "10px", color: "#cbd5e1" }}>
                This is a computer-generated document. No signature required.
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#1e40af",
                }}
              >
                {companyName}
              </div>
              <div style={{ fontSize: "10px", color: "#94a3b8" }}>Authorized Signatory</div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

AdmissionInvoice.displayName = "AdmissionInvoice";

export default AdmissionInvoice;
